<!--
  Plus's sheet, below 600 px: the apps past the bottom bar's slots, as tiles on top of everything
  else, so a sixth or seventh app lands beside them; then the account menu `UserDrawer` holds on
  desktop, with what the layout puts in the default slot (the rail's transfers and credits) under
  its header (docs/plans/mobile-navigation.md MN3). Opened by the bar's Plus through the layout
  store; closed by a drag down, the scrim, the back button, or anything in it that navigates, and
  focus goes back to Plus. Every attribute and listener the layout sets goes to the menu.
-->
<template>
  <v-bottom-sheet
    v-model="open"
    class="more-sheet app-back-dismiss"
    :close-on-back="false"
    :aria-label="t('core.navigation.more')"
    @after-leave="onAfterLeave"
  >
    <v-card ref="card" class="more-sheet__card" :style="dragStyle">
      <!-- What says the sheet can be pulled down; the drag starts anywhere at its top. -->
      <div class="more-sheet__handle" aria-hidden="true" />
      <UserMenu v-bind="$attrs" @close="close">
        <template #notifications="scope">
          <slot name="notifications" v-bind="scope" />
        </template>

        <template v-if="overflowApps.length > 0" #before>
          <nav class="drawer-section more-sheet__apps" :aria-label="t('core.navigation.apps')">
            <div class="drawer-section__header">
              <span class="drawer-section__title">{{ t('core.navigation.apps') }}</span>
            </div>
            <div class="more-sheet__apps-grid">
              <router-link
                v-for="app in overflowApps"
                :key="app.id"
                :to="{ name: app.route }"
                class="more-sheet__app"
                :class="{ 'more-sheet__app--active': isActiveApp(app) }"
                :aria-current="isActiveApp(app) ? 'page' : undefined"
                :style="{ '--app-color': app.color }"
                @click="close"
              >
                <span class="more-sheet__app-icon" v-html="app.icon" />
                <span class="more-sheet__app-label">{{ t(app.titleKey) }}</span>
              </router-link>
            </div>
          </nav>
        </template>

        <v-list nav density="compact" class="more-sheet__list">
          <slot :close="close" />
        </v-list>
      </UserMenu>
    </v-card>
  </v-bottom-sheet>
</template>

<script setup lang="ts">
import { computed, ref, type ComponentPublicInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLayoutStore } from '../../stores'
import { useMenuApps } from '../../composables/useMenuApps'
import { useSheetDrag } from '../../composables/useSheetDrag'
import { useBackDismiss } from '../../composables/useBackDismiss'
import UserMenu from './UserMenu.vue'

defineOptions({ inheritAttrs: false })

const { t } = useI18n()
const layoutStore = useLayoutStore()
const { overflowApps, isActiveApp } = useMenuApps()

const open = computed({
  get: () => layoutStore.userDrawerOpen,
  set: (value) => {
    if (value) {
      layoutStore.openUserDrawer()
    } else {
      layoutStore.closeUserDrawer()
    }
  },
})

function close() {
  open.value = false
}

useBackDismiss(() => open.value, close)

const card = ref<ComponentPublicInstance | null>(null)
const cardEl = computed(() => card.value?.$el as HTMLElement | undefined)
const { style: dragStyle, reset: resetDrag } = useSheetDrag(cardEl, close, () =>
  cardEl.value?.querySelector<HTMLElement>('.user-menu__content'),
)

// The sheet has no activator of its own: Plus, in the bar, opened it and gets focus back.
function onAfterLeave() {
  resetDrag()
  document.querySelector<HTMLElement>('[data-more-sheet-opener]')?.focus({ preventScroll: true })
}
</script>

<style lang="scss">
@use '@/styles/design-tokens' as *;

.more-sheet {
  &__card {
    // The sheet scrolls inside, under the status bar's safe area, and clears the home indicator.
    max-height: calc(100dvh - #{$space-xl});
    display: flex;
    flex-direction: column;
    padding-bottom: env(safe-area-inset-bottom);
    border-radius: $radius-xl $radius-xl 0 0 !important;
    // Springs back when a drag is let go short of closing; follows the finger without it.
    @include transition-normal(transform);
    // The drag is the sheet's, not the browser's: no overscroll bounce under the finger.
    overscroll-behavior: contain;

    @include reduced-motion {
      transition: none;
    }

    .user-menu {
      height: auto;
      flex: 1;
    }
  }

  &__handle {
    flex: none;
    width: 32px;
    height: 4px;
    margin: $space-sm auto 0;
    border-radius: $radius-full;
    background: rgba(var(--v-theme-on-surface), $opacity-disabled);
  }

  &__list {
    padding: $space-sm !important;

    .v-list-item {
      min-height: $touch-target;
      border-radius: $radius-md;
    }

    &:empty {
      display: none;
    }
  }

  // The overflow apps, laid out like the rail's entries: four to a row, as many rows as apps.
  &__apps {
    border-bottom: 1px solid rgba(var(--v-border-color), $opacity-border);
  }

  &__apps-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: $space-sm;
    padding: 0 $space-md;
  }

  &__app {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $space-xs;
    min-height: $touch-target;
    padding: $space-sm $space-xs;
    border-radius: $radius-lg;
    text-decoration: none;
    color: rgba(var(--v-theme-on-surface), $opacity-secondary);
    @include transition-fast;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.04);
    }

    @include focus-visible-only;

    &--active {
      color: rgba(var(--v-theme-on-surface), $opacity-primary);
      font-weight: $font-semibold;

      .more-sheet__app-icon {
        box-shadow: inset 0 0 0 2px var(--app-color);
      }
    }
  }

  &__app-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: $radius-lg;
    // Each app in its own colour, as on the home page's cards.
    color: var(--app-color);
    background: color-mix(in srgb, var(--app-color) 12%, transparent);

    svg {
      width: 22px;
      height: 22px;
    }
  }

  &__app-label {
    font-size: $text-xs;
    font-weight: $font-medium;
    letter-spacing: $tracking-wide;
    text-align: center;
    line-height: $leading-tight;
  }
}
</style>
